import { isValidTimeZone } from '@auctisync/shared';

import type { AuctisyncSupabase } from './client.js';

/**
 * How to reach the auction house.
 *
 * WHY THIS IS NEW DATA. The bidder design specifies a Contact screen — name,
 * address, a call button, an email button, office hours, pickup window — and an
 * audit of the schema found none of those fields anywhere. `auctions` carries
 * per-auction pickup details; `profiles` and `consignors` carry PERSONAL phone
 * numbers, which are a different thing entirely. There was no way to reach the
 * house.
 *
 * So the screen could only have been built with invented contact details, which
 * is a ghost feature and worse — a wrong phone number on a surface people reach
 * when their money is involved. `tenants.metadata.house_contact` and the two
 * RPCs behind it exist so it can be built with the truth.
 *
 * ⚠️ EVERY FIELD HERE IS PUBLIC. The reader is granted to `anon` on purpose:
 * this is what a business prints on its own catalogue. Nothing private may be
 * added to this shape — the money-adjacent settings live behind the owner-only
 * `get_invoice_settings`, and must stay there.
 *
 * EVERY FIELD IS ALSO OPTIONAL, and that is not laziness. A house that has
 * filled in a phone number and nothing else should get a Contact screen showing
 * a phone number, not a screen of blank rows and certainly not a placeholder.
 * The normaliser below turns "" into null so a screen can ask a plain question:
 * do I have this, yes or no.
 */

export interface HouseAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface HouseContact {
  phone: string | null;
  email: string | null;
  website: string | null;
  address: HouseAddress;
  /** Free text — "Mon–Fri 9–5". Deliberately not structured: opening hours are
   *  full of exceptions, and a schema for them invites a form nobody completes. */
  officeHours: string | null;
  pickupHours: string | null;
  /**
   * IANA zone the house's sales run in, e.g. `America/Chicago`.
   *
   * 🔴 THIS IS THE DEFAULT A NEW AUCTION INHERITS, and it is the whole answer to
   * Jim's complaint (2026-09-18) about doing timezone arithmetic while
   * travelling: a default taken from the BROWSER would be wrong in exactly the
   * situation that prompted it. The house says once where its sales happen, and
   * a new sale starts there no matter where the laptop is.
   */
  timezone: string | null;
  /** Anything the house wants said here — "call ahead for large items". */
  note: string | null;
}

export const EMPTY_HOUSE_CONTACT: HouseContact = {
  phone: null,
  email: null,
  website: null,
  address: { line1: null, line2: null, city: null, region: null, postalCode: null, country: null },
  officeHours: null,
  pickupHours: null,
  timezone: null,
  note: null,
};

/** Trimmed, or null. An empty string is "not set", not a value to render. */
function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/**
 * The stored jsonb, made safe to render.
 *
 * Takes `unknown` because it IS unknown: the writer stores whatever object an
 * owner's form sent, and the reader hands back raw jsonb by design so that
 * exactly one place materialises defaults. This is that place.
 */
export function normalizeHouseContact(raw: unknown): HouseContact {
  const o = record(raw);
  const addr = record(o['address']);
  return {
    phone: text(o['phone']),
    email: text(o['email']),
    website: text(o['website']),
    address: {
      line1: text(addr['line1']),
      line2: text(addr['line2']),
      city: text(addr['city']),
      region: text(addr['region']),
      postalCode: text(addr['postal_code']),
      country: text(addr['country']),
    },
    officeHours: text(o['office_hours']),
    pickupHours: text(o['pickup_hours']),
    // Only a zone the runtime actually knows survives the read. A typo stored
    // by a hand-edit must read as "unset" and fall through to the platform
    // default, never reach `Intl` — which THROWS on an unknown zone rather than
    // degrading, and would take down every screen showing a sale time.
    timezone: isValidTimeZone(text(o['timezone'])) ? text(o['timezone']) : null,
    note: text(o['note']),
  };
}

/** The address as a person would write it, or null when there is not enough of one. */
export function formatHouseAddress(address: HouseAddress): string | null {
  const cityLine = [address.city, address.region].filter(Boolean).join(', ');
  const withPostcode = [cityLine, address.postalCode].filter(Boolean).join(' ');
  const lines = [address.line1, address.line2, withPostcode].filter(
    (part): part is string => part !== null && part !== '',
  );
  return lines.length === 0 ? null : lines.join(', ');
}

/**
 * True when the house has filled in nothing at all.
 *
 * The screen needs this to say "this house has not added its contact details"
 * rather than drawing a card with no rows in it, which reads as a broken screen
 * rather than an unanswered question.
 */
export function houseContactIsEmpty(contact: HouseContact): boolean {
  return (
    contact.phone === null &&
    contact.email === null &&
    contact.website === null &&
    contact.officeHours === null &&
    contact.pickupHours === null &&
    contact.note === null &&
    formatHouseAddress(contact.address) === null
  );
}

/**
 * Read one house's public contact details.
 *
 * Never throws and never reports an error: a Contact screen that cannot reach
 * the network still has two useful routes on it (ask on the lot, open your
 * invoices), and an error banner over the top of them helps nobody. An
 * unreachable read is indistinguishable from an unfilled form, and both mean
 * the same thing to the reader — we cannot show you a phone number.
 */
export async function fetchHouseContact(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<HouseContact> {
  try {
    const res = await supabase.rpc('get_house_contact', { p_tenant_id: tenantId });
    if (res.error) return EMPTY_HOUSE_CONTACT;
    return normalizeHouseContact(res.data);
  } catch {
    return EMPTY_HOUSE_CONTACT;
  }
}

/** A `tel:` URI, or null. Strips the spaces and punctuation a dialler cannot use. */
export function telHref(phone: string | null): string | null {
  if (phone === null) return null;
  const digits = phone.replace(/[^\d+]/g, '');
  return digits === '' ? null : `tel:${digits}`;
}

/** A `mailto:` URI, or null. */
export function mailtoHref(email: string | null): string | null {
  // Deliberately loose: the house typed this, and refusing to link something
  // that merely looks unusual is worse than linking something a mail client
  // will reject with its own message.
  if (email === null || !email.includes('@')) return null;
  return `mailto:${email}`;
}
